import React from 'react';
import { graphql, useFragment, usePreloadedQuery, type EntryPointProps } from 'react-relay';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous';
import type { contextFields_nativeIssueTable$key as ProjectKey } from '@atlassian/jira-relay/src/__generated__/contextFields_nativeIssueTable.graphql';
import type { contextFields_projectDataQuery } from '@atlassian/jira-relay/src/__generated__/contextFields_projectDataQuery.graphql';
// eslint-disable-next-line jira/import/no-restricted-paths
import { useIssueCreateActions } from '../../../../controllers/issue-create-form';
import { IssueTypeDropdown } from './issue-type-dropdown';

type Props = {
	project: ProjectKey;
};

export const ContextFields = ({ project }: Props) => {
	const data = useFragment<ProjectKey>(
		graphql`
			fragment contextFields_nativeIssueTable on JiraProject {
				id
				key
				name
				avatar {
					large
				}
				...issueTypeDropdown_nativeIssueTable
			}
		`,
		project,
	);

	const { onChangeProject } = useIssueCreateActions();

	const { id, key, name, avatar } = data;
	const prevProjectId = usePrevious(id);
	if (id !== prevProjectId) {
		// Update our form data with our default project
		onChangeProject({
			id,
			key,
			name,
			avatar: avatar
				? {
						large: avatar.large,
					}
				: null,
		});
	}

	return <IssueTypeDropdown project={data} />;
};

export const ContextFieldsFallback = () => <IssueTypeDropdown project={null} />;

type Queries = {
	projectData: contextFields_projectDataQuery;
};

const ContextFieldsEntryPoint = ({
	queries: { projectData },
}: EntryPointProps<Queries, {}, {}, {}>) => {
	const data = usePreloadedQuery<contextFields_projectDataQuery>(
		graphql`
			query contextFields_projectDataQuery($cloudId: ID!, $projectKey: String!) @preloadable {
				jira @required(action: THROW) {
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) @required(action: THROW) {
						...contextFields_nativeIssueTable
					}
				}
			}
		`,
		projectData,
	);

	return <ContextFields project={data.jira.jiraProjectByKey} />;
};
export default ContextFieldsEntryPoint;
